import { Modal, Input, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { BATCH_DELETE_ADDRESS_ALIAS } from '../graphql/queries/getAddressAliasList';

interface BatchDeleteAliasModalProps {
  visible: boolean;
  onClose: () => void;
}

// 解析文本域：按英文逗号切分 → 去空白 → 去空项 → 校验均为正整数
// 返回 { ids, invalid }：ids 为合法 id 列表，invalid 为非法片段列表
const parseAliasIds = (raw: string): { ids: number[]; invalid: string[] } => {
  const ids: number[] = [];
  const invalid: string[] = [];
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .forEach((s) => {
      if (/^\d+$/.test(s) && Number(s) > 0) {
        ids.push(Number(s));
      } else {
        invalid.push(s);
      }
    });
  return { ids, invalid };
};

const BatchDeleteAliasModal: React.FC<BatchDeleteAliasModalProps> = ({ visible, onClose }) => {
  const [inputValue, setInputValue] = useState('');

  const [batchDeleteAlias, { loading }] = useMutation(BATCH_DELETE_ADDRESS_ALIAS);

  // 弹窗关闭时重置输入，避免下次打开沿用上次内容
  useEffect(() => {
    if (!visible) {
      setInputValue('');
    }
  }, [visible]);

  const handleSubmit = () => {
    const { ids, invalid } = parseAliasIds(inputValue);

    if (invalid.length > 0) {
      message.error(`存在非法别名 id：${invalid.join('、')}，请输入正整数并用英文逗号分隔`);
      return;
    }
    if (ids.length === 0) {
      message.error('请输入至少一个别名 id');
      return;
    }

    const uniqueIds = Array.from(new Set(ids));

    Modal.confirm({
      title: '确认删除',
      content: `本次将删除 ${uniqueIds.length} 条别名，确认删除？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const { data } = await batchDeleteAlias({ variables: { ids: uniqueIds } });
          const deleted = data?.batchDeleteAddressAlias ?? 0;
          message.success(`请求 ${uniqueIds.length} 条，成功删除 ${deleted} 条`);
          onClose();
        } catch (err) {
          message.error(`批量删除失败：${(err as Error).message}`);
        }
      },
    });
  };

  return (
    <Modal
      title="批量删除别名"
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="删除"
      okButtonProps={{ danger: true, loading }}
      cancelText="取消"
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>别名 id 列表（多个时用英文逗号分隔）：</div>
      <Input.TextArea
        placeholder="例如：101,102,103"
        autoSize={{ minRows: 6, maxRows: 12 }}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
    </Modal>
  );
};

export default BatchDeleteAliasModal;
